import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, RefreshCw, Mail, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { start2FA, verify2FA } from "@admin/services/adminApi";
import { ADMIN_BASE } from "@admin/constants/route";
import { toast } from "sonner";

/**
 * Admin 2FA verification screen.
 *
 * UX:
 *   - On mount, requests an OTP via POST /admin/auth/2fa/start (skips if a
 *     fresh code was sent in the last 60s, surfaces a friendly cooldown).
 *   - Renders a 6-digit pin input, "Resend code" link, and a verify button.
 *   - On success, refreshes /auth/me so the AuthContext picks up the
 *     `two_factor_verified_at` stamp, then redirects back to the route the
 *     user was originally targeting.
 *
 * Security:
 *   - Code sent via email (Resend) — never displayed in UI.
 *   - Backend rate-limits both resends (60s cooldown, 5/hour) and attempts (5).
 */
export default function Admin2FA() {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || ADMIN_BASE;

  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");

  // Inject noindex
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name", "robots"); document.head.appendChild(meta); }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", "noindex,nofollow,noarchive,nosnippet");
    return () => { if (prev != null) meta.setAttribute("content", prev); };
  }, []);

  // If 2FA already satisfied or user isn't an admin, leave immediately.
  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") {
      navigate("/", { replace: true });
      return;
    }
    if (user.two_factor_satisfied) {
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, from]);

  // Mask the email for the UI (never show full string)
  useEffect(() => {
    if (!user?.email) return;
    const [name, dom] = user.email.split("@");
    if (!dom) return;
    const masked = (name || "").slice(0, 2) + "•".repeat(Math.max(name.length - 2, 1));
    setMaskedEmail(`${masked}@${dom}`);
  }, [user]);

  // Auto-request a code on mount
  useEffect(() => {
    if (!user || user.role !== "admin" || user.two_factor_satisfied) return;
    requestCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async () => {
    setError("");
    setBusy(true);
    try {
      const r = await start2FA();
      toast.success(`Code sent to ${r.email}`);
      setCooldown(60);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send code";
      setError(detail);
      // Server returns "Please wait Ns before requesting another code." — pull seconds
      const m = String(detail).match(/(\d+)s/);
      if (m) setCooldown(Number(m[1]));
    }
    setBusy(false);
  };

  const onVerify = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      await verify2FA(code);
      toast.success("Verified — welcome back");
      await checkAuth();
      navigate(from, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Verification failed");
      setCode("");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0A2540] grid place-items-center p-6">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl" data-testid="admin-2fa-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-[#D4AF37] grid place-items-center text-[#0A2540]">
            <ShieldCheck size={22} />
          </div>
          <div>
            <p className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Two-factor verification</p>
            <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Mandatory for your role</p>
          </div>
        </div>

        <p className="text-sm text-slate-600 inline-flex items-start gap-2">
          <Mail size={14} className="mt-0.5 text-slate-400" />
          We sent a 6-digit code to <span className="font-semibold text-[#0A2540]" data-testid="admin-2fa-email">{maskedEmail}</span>.
          Code expires in 5 minutes.
        </p>

        <form onSubmit={onVerify} className="mt-6">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            placeholder="000000"
            maxLength={6}
            autoFocus
            data-testid="admin-2fa-code-input"
            className="w-full text-center text-3xl tracking-[0.6em] font-extrabold text-[#0A2540] py-4 border-2 border-slate-200 rounded-xl outline-none focus:border-[#0A2540] transition"
            style={{ fontFamily: "Poppins" }}
          />
          {error && (
            <p className="mt-3 text-xs text-rose-700 inline-flex items-center gap-1" data-testid="admin-2fa-error">
              <AlertCircle size={12} /> {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={busy || code.length !== 6}
            data-testid="admin-2fa-verify-btn"
            className="mt-4 w-full rounded-xl adv-btn-primary font-bold h-11"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            Verify and continue
          </Button>
        </form>

        <button
          onClick={requestCode}
          disabled={busy || cooldown > 0}
          data-testid="admin-2fa-resend-btn"
          className="mt-4 text-xs font-bold text-[#0A2540] hover:underline disabled:opacity-40 inline-flex items-center gap-1"
        >
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
          {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
        </button>

        <div className="mt-5 flex items-center justify-center gap-4 text-[11px] text-slate-400">
          <span>Codes expire in 5 minutes</span>
          <span>·</span>
          <span>Max 5 attempts</span>
        </div>
      </div>
    </div>
  );
}
